import { get } from 'lodash-es';
import { map, Observable, tap } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import type { ApiResponse } from '@coin-depo-types';

import { CookieService } from '@coin-depo-core/storage';
import { URL_MAP } from '@coin-depo/http';
import { environment } from '@coin-depo-config/environments/core-env/environment';
import { toFormData } from '@coin-depo-utils';
import { INotRespond, ServerNotRespondingService } from '@coin-depo-core/server-not-responding.service';
import { StorageService } from '@coin-depo-core/storage';

import * as Sentry from "@sentry/angular";

import { shouldReportApiErrorStatus, shouldReportHttpError } from './sentry-noise';

export class BaseApiService {
  protected baseUrl = environment.serverUrl;
  protected serviceName!: string;
  protected cookies!: CookieService;
  protected http!: HttpClient;
  protected router!: Router;
  protected storage!: StorageService;
  protected serverNotRespondingService!: ServerNotRespondingService;

  constructor() {}

  protected processRequest<I, O extends ApiResponse>(
    pathParam: keyof typeof URL_MAP,
    data: I
  ): Observable<O> {
    const apiUrl = this.getUrl(pathParam);
    const dataWithClickId: any = { ...data };
    if (localStorage.getItem('click_id') && dataWithClickId.client_token) {
      dataWithClickId.id = localStorage.getItem('click_id');
    }
    if (localStorage.getItem('ga_utm')) {
      dataWithClickId.ga_utm = localStorage.getItem('ga_utm');
    }
    if (localStorage.getItem('subid')) {
      dataWithClickId.subid = localStorage.getItem('subid');
    }
    if (localStorage.getItem('cdkey')) {
      dataWithClickId.cdkey = localStorage.getItem('cdkey');
    }
    if (localStorage.getItem('telegram_id')) {
      dataWithClickId.telegram_id = localStorage.getItem('telegram_id')
    }
    if (localStorage.getItem('twclid')) {
      dataWithClickId.twclid = localStorage.getItem('twclid')
    }
    return this.http
      .post<any>(apiUrl, toFormData(dataWithClickId))
      .pipe(
        tap({
          next: (res: O): void => {            
            // N1: report only genuine API errors; expected login/business
            // failures (see sentry-noise.ts allowlist) are silenced.
            if (shouldReportApiErrorStatus(res)) {
              this.storage.get('userDetails')
              Sentry.captureMessage('API returned error status', {
                level: 'error',
                tags: {
                  type: 'api_response_error',
                },
                extra: {
                  api_url: apiUrl,
                  user_id: this.storage.get('userDetails')?.user_id || '',
                  parent_id: res?.parent_id,
                  response: res,
                },
              });
            }
            if (res.code === 'COINDEPO_LISTING') {
              this.router.navigate(['/downtime']);
            }

            if (res.status === 'success') {
              if (dataWithClickId.id && dataWithClickId.client_token) {
                localStorage.removeItem('click_id');
              }
              if (dataWithClickId.ga_utm && dataWithClickId.client_token) {
                localStorage.removeItem('ga_utm');
              }
              if (dataWithClickId.subid && dataWithClickId.client_token) {
                localStorage.removeItem('subid');
                this.cookies.delete('_kt_subid_cookie')
              }
              if (dataWithClickId.cdkey && dataWithClickId.client_token) {
                localStorage.removeItem('cdkey');
                this.cookies.delete('cdkey');
              }
              if (dataWithClickId.telegram_id && dataWithClickId.client_token) {
                localStorage.removeItem('telegram_id');
                this.cookies.delete('telegram_id');
              }
            }
            this.checkForRelogin(res.msg as string, res?.code);
            this.serverResponding();
          },
          error: (err: any) => {            
            // N3: skip transient network aborts (status 0). The UI overlay and
            // relogin below still run unchanged.
            if (shouldReportHttpError(err)) {
              Sentry.captureException(err, {
                tags: {
                  type: 'api_error',
                },
                extra: {
                  api_url: apiUrl,
                  user_id: this.storage.get('userDetails')?.user_id || '',
                  parent_id: err.parent_id || '',
                  status: err.status || '',
                  code: err.code || '',
                  full_error: err,
                },
              });
            }
            if (err.status === 0 || err.status === 502) {
              const errorData = {
                code: err.code ?? err.error_code,
                parent_id: err.parent_id ?? err.ray_id
              }
              this.serverNotResponding(errorData);
            } else {
              this.serverResponding();
            }
            if (get(err, 'error.status', null) === 'error') {
              this.checkForRelogin(err.error.msg, err.error.code);
            }
          },
        })
      );
  }

  protected processFileRequest<I extends ApiResponse>(
    pathParam: keyof typeof URL_MAP,
    data: I
  ): Observable<any> {
    const apiUrl = this.getUrl(pathParam);
    return this.http
      .post(apiUrl, toFormData(data), {
        responseType: 'text',
      })
      .pipe(
        tap({
          error: (err: any) => {
            // N3: skip transient network aborts (status 0).
            if (shouldReportHttpError(err)) {
              Sentry.captureException(err, {
                tags: {
                  type: 'api_error',
                },
                extra: {
                  api_url: apiUrl,
                  user_id: this.storage.get('userDetails')?.user_id || '',
                  parent_id: err.parent_id || '',
                  status: err.status || '',
                  code: err.code || '',
                  full_error: err,
                },
              });
            }
            if (get(err, 'error.status', null) === 'error') {
              this.checkForRelogin(err.error.msg, err.error.code);
            }
          },
        }),
        map((text: string) => new Blob([text], { type: 'text/plain' }))
      );
  }

  protected processBlobRequest<I extends ApiResponse>(
    pathParam: keyof typeof URL_MAP,
    data: I
  ): Observable<any> {
    const apiUrl = this.getUrl(pathParam);
    return this.http
      .post(apiUrl, toFormData(data), {
        responseType: 'blob',
      })
      .pipe(
        tap({
          error: (err: any) => {
            // N3: skip transient network aborts (status 0).
            if (shouldReportHttpError(err)) {
              Sentry.captureException(err, {
                tags: {
                  type: 'api_error',
                },
                extra: {
                  api_url: apiUrl,
                  user_id: this.storage.get('userDetails')?.user_id || '',
                  parent_id: err.parent_id || '',
                  status: err.status || '',
                  code: err.code || '',
                  full_error: err,
                },
              });
            }
            if (get(err, 'error.status', null) === 'error') {
              this.checkForRelogin(err.error.msg, err.error.code);
            }
          },
        })
      );
  }

  protected getUrl(urlPart: keyof typeof URL_MAP): string {
    const path = get(URL_MAP, urlPart);
    return `${this.baseUrl}${path}`;
  }

  protected checkForRelogin(msg: string, code?: string): void {
    if (
      code &&
      (code === 'RELOGIN_REQUIRED' ||
        code === 'WRONG_PASSWORD' ||
        code === 'USER_NOT_ACTIVE' ||
        code === 'LOGOUT_SUCCESS' ||
        (code && code === 'AUTH_ERROR'))
    ) {
      this.cookies.deleteAll();
      this.storage.clear();
      this.router.navigate(['/', 'auth', 'login']);
    }
  }

  private serverNotResponding(errorData: INotRespond) {
    if (!this.serverNotRespondingService.hasErrorSnapshot()) {
      this.serverNotRespondingService.enableFullscreenError(errorData);
    }
  }

  private serverResponding() {
    if (this.serverNotRespondingService.hasErrorSnapshot()) {
      this.serverNotRespondingService.disableFullscreenError();
    }
  }
}
